import {
  isValidEmail,
  isValidPhone,
  getTerminalDateByOffset,
} from "../../../../utils/utils";
import {
  FORM_VALIDATION_MSG,
  MAX_LENGTH_CONSTRAINTS,
  MIN_LENGTH_CONSTRAINTS,
  SEARCH_TYPE,
} from "../constants";
import {ONLY_NUMBER_REGEX} from "../../../constRegex"
import {
  convertDateTimeToTimeString,
  getDateWithUtcHoursAndMinFromTimeString,
} from "../../../../../../dataGrid/gridUtilities";
import { format } from "date-fns";
import { PICKUP_MANAGEMENT_CONFIG } from "../config";
import { maxBy } from "lodash";

export const weekday = {
  1: "monday",
  2: "tuesday",
  3: "wednesday",
  4: "thursday",
  5: "friday",
  6: "saturday",
  7: "sunday",
};

const getFormattedshipperContactPhone = (shipperContactPhone) => {
  return shipperContactPhone === "0" ? "" : shipperContactPhone;
}

const getDefaultShipper = (data) => {
  return {
    shipperName: data?.shipperName ?? "",
    shipperAccountNumber: data?.shipperAccountNumber ?? "",
    shipperAddress1: data?.shipperAddress1 ?? "",
    shipperAddress2: data?.shipperAddress2 ?? "",
    shipperCity: data?.shipperCity ?? "",
    shipperState: data?.shipperState ?? "",
    shipperZip: data?.shipperZip ?? "",
    shipperContactName: data?.shipperContactName ?? "",
    shipperContactPhone: data?.shipperPhoneNumber
      ? getFormattedshipperContactPhone(data?.shipperPhoneNumber)
      : "",
    shipperEmail: data?.shipperEmail ?? "",
    rmaxShipperCode: data?.rmaxShipperCode ?? "",
    pickupWindow: data?.pickupWindow ?? {},
    shipperSpecialInstructions : data?.shipperSpecialInstructions
  };
};

export const getShipperPickupWindow = (pickupDate, shipperPickupWindow = {}) => {
  if (!pickupDate) {
    return null;
  }
  const dayOfWeek = weekday[pickupDate.getDay()];
  return {
    ...(shipperPickupWindow?.[dayOfWeek] || {}),
    dayOfWeek,
  };
}

const getDefaultPickupDateTimeInfo = (data, terminalOffset = "00:00") => {
  const pickupDate = data?.pickupDate
    ? new Date(data?.pickupDate + "T00:00:00")
    : new Date(getTerminalDateByOffset(new Date(), terminalOffset));
  const shipperPickupWindow = getShipperPickupWindow(pickupDate, data?.pickupWindow);
 const  shipperPickupReadyTime = shipperPickupWindow?.pickupReadyTime === "00:00" ? null :  shipperPickupWindow?.pickupReadyTime;
  let pickupReadyTime =
    data?.pickupReadyTime || shipperPickupReadyTime;
  let pickupCloseTime =
    data?.pickupCloseTime || shipperPickupWindow?.window?.[0]?.closeTime || null;
  return {
    readyTime:
      getDateWithUtcHoursAndMinFromTimeString(pickupReadyTime) ??
      format(new Date(), "yyyy-MM-dd'T'HH:mm:00.000Z"),
    closeTime: getDateWithUtcHoursAndMinFromTimeString(pickupCloseTime) ?? "",
    pickupDate,
    route : data?.staticRoute || data?.defaultDoor || ""
  };
};

const getSelectedShipperData = (shipper) => {
  return {
    shipperInfo: getDefaultShipper(shipper),
    pickupDateTimeInfo: getDefaultPickupDateTimeInfo(shipper),
    specialInstructions: shipper?.shipperSpecialInstructions ?? "",
  };
};

const getDefaultShipperError = () => {
  return {
    shipperName: null,
    shipperAccountNumber: null,
    shipperAddress1: null,
    shipperAddress2: null,
    shipperCity: null,
    shipperState: null,
    shipperZip: null,
    shipperContactName: null,
    shipperContactPhone: null,
    shipperEmail: null,
  };
};

const getDefaultPickupDateTimeErrorInfo = () => {
  return {
    readyTime: null,
    closeTime: null,
    pickupDate: null,
    route : null,
  };
};
const getShipperNumberError = (value) => {
  if (value && !isValidPhone(value)) {
    return FORM_VALIDATION_MSG.INVALID_PHONE_NUMBER;
  }
  return null;
};

const getShipperEmailError = (value) => {
  if (value && !isValidEmail(value)) {
    return FORM_VALIDATION_MSG.INVALID_EMAIL;
  }
  return null;
};

const getRequiredFieldError = (value) => {
  if (!value) {
    return FORM_VALIDATION_MSG.REQUIRED;
  }
  return null;
};

const getCloseTimeError = (value, readyTime) => {
  if (!value) {
    return FORM_VALIDATION_MSG.REQUIRED;
  }
  if (new Date(value) <= new Date(readyTime)) {
    return `Close Time should be greater than ${convertDateTimeToTimeString(
      readyTime
    )}`;
  }
  return null;
};

const getZipFieldError = (value, shipperDetails) => {
  if (!value) {
    return getRequiredFieldError(value);
  }
  if (
    shipperDetails &&
    !(shipperDetails.shipperCity || shipperDetails.shipperState)
  ) {
    return FORM_VALIDATION_MSG.ZIP_CODE.INVALID_VALUE;
  }
  return null;
};

const getFieldError = ({
  field,
  value,
  readyTime = null,
  mode = null,
  terminalOffset = null,
  shipperDetails = null,
}) => {
  switch (field) {
    case "shipperContactPhone":
      return getShipperNumberError(value);
    case "shipperEmail":
      return getShipperEmailError(value);
    case "shipperName":
      return getRequiredFieldError(value);
    case "shipperZip":
      return getZipFieldError(value, shipperDetails);
    case "shipperAddress1":
      return getRequiredFieldError(value);
    case "shipperState":
      return getRequiredFieldError(value);
    case "shipperCity":
      return getRequiredFieldError(value);
    case "readyTime":
      return getRequiredFieldError(value);
    case "closeTime":
      return getCloseTimeError(value, readyTime);
    case "pickupDate":
      return PICKUP_MANAGEMENT_CONFIG[mode]?.getPickupDateError(
        value,
        terminalOffset
      );
    default:
      return null;
  }
};

const getShipperErrorInfo = (shipperDetails, readyTime = "") => {
  let errorInfo = {};
  if (shipperDetails) {
    Object.keys(shipperDetails).forEach((fieldName) => {
      errorInfo[fieldName] = getFieldError({
        field: fieldName,
        value: shipperDetails[fieldName],
        readyTime,
        shipperDetails,
      });
    });
  }
  return errorInfo;
};

const getPickupDateTimeErrorInfo = ({
  pickupDateTimeInfo,
  terminalOffset,
  readyTime = "",
  mode,
}) => {
  let errorInfo = {};
  if (pickupDateTimeInfo) {
    Object.keys(pickupDateTimeInfo).forEach((fieldName) => {
      errorInfo[fieldName] = getFieldError({
        field: fieldName,
        value: pickupDateTimeInfo[fieldName],
        readyTime,
        mode,
        terminalOffset,
      });
    });
  }
  return errorInfo;
};

const getFormattedShipperNameAndAccNumber = (
  shipperName,
  shipperAccountNumber
) => {
  if (shipperName) {
    return `${shipperName} ${
      shipperAccountNumber ? `(${shipperAccountNumber})` : ""
    }`;
  }
  return null;
};

const getSearchParamsByType = (
  terminalCode,
  searchBy,
  selectedTerminalCode,
  searchInputValue
) => {
  switch (searchBy) {
    case SEARCH_TYPE.NAME:
      return `terminalCode=${terminalCode}&pickupTerminal=${selectedTerminalCode}&shipperName=${encodeURIComponent(
        searchInputValue
      )}`;
    case SEARCH_TYPE.ACCOUNT_CODE:
      return `terminalCode=${terminalCode}&pickupTerminal=${selectedTerminalCode}&accountNumber=${searchInputValue}`;
    case SEARCH_TYPE.PHONE:
      return `terminalCode=${terminalCode}&pickupTerminal=${selectedTerminalCode}&shipperPhoneNumber=${searchInputValue}`;
    default: //shipper address is default search
      return `terminalCode=${terminalCode}&pickupTerminal=${selectedTerminalCode}&shipperAddress=${encodeURIComponent(
        searchInputValue
      )}`;
  }
};

const isValidSearchString = (searchBy, searchString) => {
  switch (searchBy) {
    case SEARCH_TYPE.NAME:
      return searchString.length > MIN_LENGTH_CONSTRAINTS.CREATE_PICKUP_FORM.SHIPPER_NAME;
    case SEARCH_TYPE.ACCOUNT_CODE:
      return searchString.length === MAX_LENGTH_CONSTRAINTS.CREATE_PICKUP_FORM.ACCOUNT;
    case SEARCH_TYPE.PHONE:
      return searchString.length === MAX_LENGTH_CONSTRAINTS.CREATE_PICKUP_FORM.SHIPPER_CONTACT_PHONE;
    default: //shipper address is default search
      return searchString.length > MIN_LENGTH_CONSTRAINTS.CREATE_PICKUP_FORM.SHIPPER_ADDRESS;
  }
};

const getSearchProps = (searchBy) => {
  switch (searchBy) {
    case SEARCH_TYPE.NAME:
      return {
        placeholder: "Enter shipper's name",
        inputProps: {
          maxLength:
            MAX_LENGTH_CONSTRAINTS.CREATE_PICKUP_FORM.SHIPPER_CONTACT_NAME,
        },
      };
    case SEARCH_TYPE.ACCOUNT_CODE:
      return {
        placeholder: "Enter shipper's account code",
        inputProps: {
          pattern: ONLY_NUMBER_REGEX,
          maxLength: MAX_LENGTH_CONSTRAINTS.CREATE_PICKUP_FORM.ACCOUNT,
        },
      };
    case SEARCH_TYPE.PHONE:
      return {
        placeholder: "Enter shipper's phone number",
        inputProps: {
          pattern: ONLY_NUMBER_REGEX,
          maxLength:
            MAX_LENGTH_CONSTRAINTS.CREATE_PICKUP_FORM.SHIPPER_CONTACT_PHONE,
        },
      };
    default: //shipper address is default search
      return {
        placeholder: "Enter shipper's address",
        inputProps: {
          maxLength:
            MAX_LENGTH_CONSTRAINTS.CREATE_PICKUP_FORM.SHIPPER_ADDRESS,
        },
      };
  }
};

const getTerminalFieldError = (value) => {
  return getRequiredFieldError(value);
};

const getSearchFieldError = (selectedTerminal) => {
  if (!selectedTerminal) {
    return FORM_VALIDATION_MSG.SEARCH_FIELD_ERROR.TERMINAL_REQUIRED;
  }
};

const getlatestReadyCloseTime = (pickups) => {
  const { pickupReadyTime } = maxBy(
    pickups,
    (pickup) => pickup.pickupReadyTime
  );
  const { pickupCloseTime } = maxBy(
    pickups,
    (pickup) => pickup.pickupCloseTime
  );
  return {
    pickupReadyTime,
    pickupCloseTime,
  };
}

export {
  getDefaultShipper,
  getDefaultPickupDateTimeInfo,
  getDefaultPickupDateTimeErrorInfo,
  getDefaultShipperError,
  getFieldError,
  getShipperErrorInfo,
  getFormattedShipperNameAndAccNumber,
  getSearchParamsByType,
  getPickupDateTimeErrorInfo,
  getSelectedShipperData,
  getTerminalDateByOffset,
  getSearchProps,
  getTerminalFieldError,
  getSearchFieldError,
  isValidSearchString,
  getlatestReadyCloseTime
};
